import { notifications } from '@mantine/notifications';
import { myFetch } from '@commons/fetch';

export interface QueueStats {
  overview: {
    status: 'healthy' | 'degraded';
    pendingJobs: number;
    activeConsumers: number;
    processingRate: string;
    issues: string[] | null;
  };
  health: {
    initialized: boolean;
    rabbitmqConnected: boolean;
    rabbitmqInitialized: boolean;
    rabbitmqConnectionState: string;
    channelState: string;
    consumerRunning: boolean;
    queueName: string;
    statusQueueName: string;
    healthCheck: {
      healthCheckRunning: boolean;
      healthCheckFailureCount: number;
      checkIntervalMs: number;
    };
    note: string;
  };
  metrics: {
    pendingJobs: number;
    readyJobs: number;
    unacknowledgedJobs: number;
    consumers: number;
    consumerUtilization: number;
    totalProcessed: number;
    totalPublished: number;
    totalDelivered: number;
    messageRate: {
      publish: number;
      deliver: number;
      ack: number;
    };
  };
  queues: {
    main: any;
    status: any;
  };
  system: {
    timestamp: string;
    nodeVersion: string;
    uptime: number;
    memoryUsage: {
      used: number;
      total: number;
      unit: string;
    };
  };
  architecture: {
    mode: string;
    components: string[];
    tracking: string;
  };
}

export const makeApiCall = async (endpoint: string, method: string = 'GET') => {
  try {
    const data = await myFetch(`${endpoint}`, {
      method,
    });
    return data;
  } catch (error) {
    console.error(`Error calling ${endpoint}:`, error);
    notifications.show({
      title: 'Error',
      message: error instanceof Error ? error.message : 'Unknown error',
      color: 'red',
      autoClose: 3000,
    });
    throw error;
  }
};

export const getQueueHealth = async (start: () => void, end: () => void) => {
  try {
    start();
    const data = await makeApiCall('/folders/patch/health');
    const health = data.data;

    const allHealthy =
      health.initialized && health.rabbitmqConnected && health.consumerRunning;

    notifications.show({
      title: allHealthy ? '✅ Queue Healthy' : '⚠️ Queue Issues Detected',
      message: `Init: ${health.initialized ? '✅' : '❌'} RabbitMQ: ${health.rabbitmqConnected ? '✅' : '❌'} Consumer: ${health.consumerRunning ? '✅' : '❌'}`,
      color: allHealthy ? 'green' : 'orange',
      autoClose: 5000,
    });
  } catch (error) {
    console.error('Failed to get queue health:', error);
  } finally {
    end();
  }
};

export const resetQueues = async (start: () => void, end: () => void) => {
  try {
    start();
    const data = await makeApiCall('/folders/patch/reset-queues', 'GET');
    notifications.show({
      title: 'Queues Reset',
      message: data.message,
      color: 'green',
      autoClose: 3000,
    });
  } catch (error) {
    console.error('Failed to reset queues:', error);
  } finally {
    end();
  }
};

export const forceReconnect = async (start: () => void, end: () => void) => {
  try {
    start();
    const data = await makeApiCall('/folders/patch/reconnect', 'POST');
    notifications.show({
      title: 'Force Reconnect',
      message: data.message,
      color: 'green',
      autoClose: 3000,
    });
  } catch (error) {
    console.error('Failed to force reconnect:', error);
  } finally {
    end();
  }
};

export const restartConsumer = async (start: () => void, end: () => void) => {
  try {
    start();
    const data = await makeApiCall('/folders/patch/restart-consumer', 'POST');
    notifications.show({
      title: 'Consumer Restart',
      message: data.message,
      color: 'green',
      autoClose: 3000,
    });
  } catch (error) {
    console.error('Failed to restart consumer:', error);
  } finally {
    end();
  }
};

export const startMultipleConsumers = async (
  start: () => void,
  end: () => void
) => {
  try {
    start();
    const data = await makeApiCall(
      '/folders/patch/start-multiple-consumers',
      'POST'
    );
    notifications.show({
      title: 'Multiple Consumers',
      message: data.message,
      color: 'green',
      autoClose: 3000,
    });
  } catch (error) {
    console.error('Failed to start multiple consumers:', error);
  } finally {
    end();
  }
};

export const resetLockedFields = async (start: () => void, end: () => void) => {
  try {
    start();
    const data = await makeApiCall(
      '/folders/files/reset-locked-fields',
      'POST'
    );
    const stats = data.data;
    notifications.show({
      title: 'Locked Fields Reset',
      message: `Reset ${stats.totalReset} locked field(s) across ${stats.foldersProcessed} folder(s) and ${stats.filesProcessed} file(s)`,
      color: 'green',
      autoClose: 5000,
    });
  } catch (error) {
    console.error('Failed to reset locked fields:', error);
  } finally {
    end();
  }
};

export const triggerLoop = async (start: () => void, end: () => void) => {
  try {
    start();
    const data = await makeApiCall('/trigger-loop', 'POST');
    notifications.show({
      title: 'Loop Triggered',
      message: data.message || 'Loop triggered successfully',
      color: 'green',
      autoClose: 3000,
    });
  } catch (error) {
    console.error('Failed to trigger loop:', error);
  } finally {
    end();
  }
};
